"use client";

/**
 * LOT 208 — Cloche + boîte de réception des notifications.
 *
 * Affichée dans la barre du bas (mobile) et dans la barre latérale (desktop).
 * Les deux sont montées en même temps, chacune masquée selon la largeur
 * d'écran — d'où le signal qui les tient à jour l'une l'autre.
 *
 * Chaque notification porte une icône qui S'ALLUME tant qu'elle n'est pas
 * lue : halo de sa couleur qui respire, petites bulles qui s'en échappent.
 * Toucher l'icône bascule lue / non lue. Glisser la ligne vers la gauche la
 * supprime, la corbeille apparaissant dessous — le geste d'iOS, à la souris
 * comme au doigt : les événements de pointeur couvrent les deux (l'HTML5
 * drag-and-drop, lui, ne marche ni au doigt ni de façon fiable).
 *
 * Ouvrir une notification la marque lue. Le lien suit la navigation de l'app,
 * sans recharger la page (un <a href> coupait la requête « marquer lue »).
 */

import { type CSSProperties, useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import {
  listNotifications,
  getUnreadCount,
  markNotificationRead,
  markNotificationUnread,
  markAllNotificationsRead,
  deleteNotification,
  deleteAllNotifications,
  type NotificationItem,
} from "@/app/notifications/actions";

type Meta = { emoji: string; teinte: string };

/** Emoji et couleur d'illumination par type de notification. */
const TYPE_LABELS: Record<string, Meta> = {
  budget_85: { emoji: "📊", teinte: "#F59E0B" },
  coussin: { emoji: "🪴", teinte: "#3B82F6" },
  revenue_reminder: { emoji: "💰", teinte: "#8B5CF6" },
  fixed_expense_check: { emoji: "📌", teinte: "#F43F5E" },
  stock_low: { emoji: "📦", teinte: "#F97316" },
  abo_overdue: { emoji: "🔁", teinte: "#0EA5E9" },
  csv_import_reminder: { emoji: "🧾", teinte: "#14B8A6" },
  bank_payment_reminder: { emoji: "🏦", teinte: "#6366F1" },
  vehicle_km_reminder: { emoji: "🚗", teinte: "#38BDF8" },
  vehicle_km_actuel_reminder: { emoji: "🚗", teinte: "#38BDF8" },
  vehicle_entretien: { emoji: "🚗", teinte: "#38BDF8" },
  velo_entretien: { emoji: "🚲", teinte: "#10B981" },
  dsm_reminder: { emoji: "📋", teinte: "#8B5CF6" },
  groupe_amis_invitation: { emoji: "👥", teinte: "#2D9D5C" },
  groupe_amis_request: { emoji: "🙋", teinte: "#F59E0B" },
  net_worth_share: { emoji: "🤝", teinte: "#10B981" },
  net_worth_share_accepted: { emoji: "✅", teinte: "#22C55E" },
  net_worth_share_declined: { emoji: "🚫", teinte: "#EF4444" },
  banque_share: { emoji: "🏦", teinte: "#6366F1" },
  banque_share_accepted: { emoji: "✅", teinte: "#22C55E" },
  vie_commune_depense: { emoji: "🧾", teinte: "#EC4899" },
  vie_commune_fixe: { emoji: "🏠", teinte: "#E879F9" },
  vie_commune_recap: { emoji: "📬", teinte: "#A855F7" },
  vie_commune_remboursement: { emoji: "💸", teinte: "#10B981" },
  vie_commune_tache: { emoji: "🧽", teinte: "#3FB692" },
};
const META_DEFAUT: Meta = { emoji: "🔔", teinte: "#E5A336" };

/** Part de la largeur au-delà de laquelle relâcher la ligne la supprime. */
const SEUIL_SUPPRESSION = 0.38;

const CSS = `
.notif-icone { transition: background-color .4s ease, box-shadow .5s ease, transform .18s ease; background: color-mix(in srgb, var(--g-fg) 6%, transparent); }
.notif-icone:active { transform: scale(.9); }
.notif-icone[data-allumee="oui"] { background: color-mix(in srgb, var(--teinte) 20%, var(--g-surface)); animation: notif-respire 3s ease-in-out infinite; }
@keyframes notif-respire {
  0%, 100% { box-shadow: 0 0 0 1.5px color-mix(in srgb, var(--teinte) 50%, transparent), 0 0 8px 0 color-mix(in srgb, var(--teinte) 30%, transparent); }
  50% { box-shadow: 0 0 0 1.5px color-mix(in srgb, var(--teinte) 85%, transparent), 0 0 18px 3px color-mix(in srgb, var(--teinte) 50%, transparent); }
}
.notif-emoji { transition: filter .4s ease, opacity .4s ease, transform .4s ease; }
.notif-icone[data-allumee="non"] .notif-emoji { filter: grayscale(1); opacity: .45; }
.notif-icone[data-allumee="oui"] .notif-emoji { transform: scale(1.06); }
.notif-bulles, .notif-salve { position: absolute; inset: 0; pointer-events: none; }
.notif-bulles i, .notif-salve i { position: absolute; left: 50%; top: 35%; width: var(--t, 4px); height: var(--t, 4px); border-radius: 9999px; background: var(--teinte); opacity: 0; }
.notif-bulles i { animation: notif-bulle 3s ease-out infinite; }
.notif-bulles i:nth-child(1) { --x: -10px; --t: 4.5px; animation-delay: 0s; }
.notif-bulles i:nth-child(2) { --x: 8px; --t: 3px; animation-delay: .75s; }
.notif-bulles i:nth-child(3) { --x: -3px; --t: 3.5px; animation-delay: 1.5s; }
.notif-bulles i:nth-child(4) { --x: 11px; --t: 2.5px; animation-delay: 2.25s; }
@keyframes notif-bulle {
  0% { transform: translate(calc(-50% + var(--x) * .2), 0) scale(.4); opacity: 0; }
  25% { opacity: .9; }
  100% { transform: translate(calc(-50% + var(--x)), -18px) scale(1); opacity: 0; }
}
.notif-salve i { animation: notif-salve .9s cubic-bezier(.2,.8,.3,1) forwards; }
.notif-salve i:nth-child(1) { --x: -14px; --y: -14px; --t: 5px; }
.notif-salve i:nth-child(2) { --x: 12px; --y: -16px; --t: 4px; animation-delay: .04s; }
.notif-salve i:nth-child(3) { --x: -4px; --y: -20px; --t: 4.5px; animation-delay: .08s; }
.notif-salve i:nth-child(4) { --x: 17px; --y: -6px; --t: 3px; animation-delay: .02s; }
.notif-salve i:nth-child(5) { --x: -18px; --y: -4px; --t: 3px; animation-delay: .06s; }
.notif-salve i:nth-child(6) { --x: 5px; --y: -19px; --t: 3.5px; animation-delay: .1s; }
@keyframes notif-salve {
  0% { transform: translate(-50%, 0) scale(.3); opacity: 0; }
  30% { opacity: 1; }
  100% { transform: translate(calc(-50% + var(--x)), var(--y)) scale(1); opacity: 0; }
}
.notif-extinction { position: absolute; inset: 0; border-radius: 9999px; pointer-events: none; box-shadow: 0 0 0 2px var(--teinte); animation: notif-extinction .55s ease-out forwards; }
@keyframes notif-extinction { from { transform: scale(1); opacity: .7; } to { transform: scale(1.7); opacity: 0; } }
`;

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `il y a ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `il y a ${diffD}j`;
  return d.toLocaleDateString("fr-FR");
}

const SIGNAL = "gestic:notifications";
type Signal = { source: string; unread: number };

/** Lien interne → navigation dans l'app, sans recharger la page. */
function estInterne(url: string): boolean {
  return url.startsWith("/") && !url.startsWith("//");
}

export function NotificationsBell() {
  const source = useId();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<NotificationItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmerVider, setConfirmerVider] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // ── Synchro fiable entre les icônes et le badge ──
  // Les actions serveur passent l'une après l'autre. Un recomptage lancé avant
  // un clic suivant revenait APRÈS lui et écrasait le badge avec un état périmé :
  // d'où la latence puis l'inversion entre l'icône et la pastille. Désormais :
  //  - chaque notification garde l'état VOULU (le dernier clic gagne), avec une
  //    seule écriture à la fois par notification ;
  //  - le badge suit les gestes tout de suite, et n'accepte un compte serveur
  //    (recomptage, relevé des 30 s, autre cloche) que si aucune écriture n'est
  //    en cours et que rien n'a changé localement depuis que ce compte est parti.
  const generation = useRef(0);
  const ecrituresEnCours = useRef(0);
  const voulu = useRef(new Map<string, boolean>());
  const enVol = useRef(new Set<string>());
  const stable = () => ecrituresEnCours.current === 0;

  // Compteur au montage, puis toutes les 30 s pour capter les nouvelles notifs.
  useEffect(() => {
    let cancelled = false;
    const relever = () => {
      const gen = generation.current;
      void getUnreadCount().then((n) => {
        if (!cancelled && gen === generation.current && stable()) setUnread(n);
      });
    };
    relever();
    const interval = setInterval(relever, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // L'autre cloche a changé quelque chose : badge à jour, liste relue à l'ouverture.
  useEffect(() => {
    function onSignal(e: Event) {
      const d = (e as CustomEvent<Signal>).detail;
      if (!d || d.source === source) return;
      if (stable()) setUnread(d.unread);
      setItems(null);
    }
    window.addEventListener(SIGNAL, onSignal);
    return () => window.removeEventListener(SIGNAL, onSignal);
  }, [source]);

  // Click outside → ferme.
  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  /** Recompte côté serveur — la vérité — mais seulement si rien n'a bougé entre-temps ; prévient l'autre cloche. */
  async function recompter() {
    const gen = generation.current;
    const n = await getUnreadCount();
    if (gen !== generation.current || !stable()) return; // un geste est passé : son propre recomptage suivra
    setUnread(n);
    window.dispatchEvent(new CustomEvent<Signal>(SIGNAL, { detail: { source, unread: n } }));
  }

  async function chargerListe(avecSpinner: boolean) {
    if (avecSpinner) setLoading(true);
    const list = await listNotifications(50);
    setItems(list);
    setLoading(false);
  }

  function handleOpen() {
    const ouvrir = !open;
    setOpen(ouvrir);
    setConfirmerVider(false);
    // Liste relue à chaque ouverture : une notification arrivée entre-temps
    // doit apparaître, et « Tout supprimer » doit porter sur ce qu'on voit.
    if (ouvrir) {
      void chargerListe(items === null);
      void recompter();
    }
  }

  /** Affiche tout de suite le nouvel état d'une notification (icône + badge). */
  function afficherEtat(id: string, lue: boolean, etaitLue: boolean) {
    generation.current += 1;
    voulu.current.set(id, lue);
    setItems((prev) => prev?.map((x) => (x.id === id ? { ...x, read_at: lue ? (x.read_at ?? new Date().toISOString()) : null } : x)) ?? null);
    if (lue !== etaitLue) setUnread((u) => Math.max(0, u + (lue ? -1 : 1)));
  }

  /**
   * Écrit en base l'état voulu d'une notification. Une seule écriture à la
   * fois par notification : si l'utilisateur rebascule pendant qu'elle part,
   * on réécrit à l'arrivée jusqu'à ce que la base corresponde au dernier clic.
   */
  async function ecrireEtat(id: string, bruyant: boolean) {
    if (enVol.current.has(id)) return; // l'écriture en cours relira l'état voulu en finissant
    enVol.current.add(id);
    ecrituresEnCours.current += 1;
    let echec: string | null = null;
    try {
      for (let tour = 0; tour < 6; tour++) {
        const cible = voulu.current.get(id);
        if (cible === undefined) break;
        const r = cible ? await markNotificationRead(id) : await markNotificationUnread(id);
        if (!r.ok) { echec = r.error ?? "erreur inconnue"; break; }
        if (voulu.current.get(id) === cible) break; // la base suit le dernier clic
      }
    } finally {
      enVol.current.delete(id);
      ecrituresEnCours.current -= 1;
    }
    if (echec) {
      voulu.current.delete(id);
      if (bruyant) alert(`Impossible d'enregistrer l'état de la notification : ${echec}`);
      else console.warn("[notifications] état non enregistré :", echec);
      generation.current += 1;
      await chargerListe(false); // retour à l'état réel
    }
    if (stable()) await recompter();
  }

  async function marquerLue(n: NotificationItem, bruyant: boolean) {
    if (n.read_at) return;
    afficherEtat(n.id, true, false);
    await ecrireEtat(n.id, bruyant);
  }

  /** L'icône de la ligne : allumée = non lue. La toucher bascule l'état. */
  function basculerLue(n: NotificationItem) {
    const etaitLue = !!n.read_at;
    afficherEtat(n.id, !etaitLue, etaitLue);
    void ecrireEtat(n.id, true);
  }

  function ouvrirLien(n: NotificationItem) {
    // Navigation dans l'app : la page reste, la requête va au bout.
    void marquerLue(n, false);
    setOpen(false);
  }

  async function ouvrirLienExterne(n: NotificationItem, url: string) {
    // La page va se décharger : on attend que la lecture soit enregistrée.
    await marquerLue(n, false);
    setOpen(false);
    window.location.assign(url);
  }

  async function supprimer(id: string) {
    const cible = items?.find((n) => n.id === id);
    generation.current += 1;
    voulu.current.delete(id);
    setItems((prev) => prev?.filter((n) => n.id !== id) ?? null);
    if (cible && !cible.read_at) setUnread((u) => Math.max(0, u - 1));
    ecrituresEnCours.current += 1;
    try {
      const r = await deleteNotification(id);
      if (!r.ok) {
        alert(`Suppression impossible : ${r.error ?? "erreur inconnue"}`);
        await chargerListe(false);
      }
    } finally {
      ecrituresEnCours.current -= 1;
    }
    if (stable()) await recompter();
  }

  async function toutMarquerLu() {
    setOccupe(true);
    generation.current += 1;
    for (const k of voulu.current.keys()) voulu.current.set(k, true);
    const maintenant = new Date().toISOString();
    setItems((prev) => prev?.map((n) => (n.read_at ? n : { ...n, read_at: maintenant })) ?? null);
    setUnread(0);
    ecrituresEnCours.current += 1;
    try {
      const r = await markAllNotificationsRead();
      if (!r.ok) {
        alert(`Impossible de tout marquer comme lu : ${r.error ?? "erreur inconnue"}`);
        await chargerListe(false);
      }
    } finally {
      ecrituresEnCours.current -= 1;
    }
    if (stable()) await recompter();
    setOccupe(false);
  }

  async function toutSupprimer() {
    setConfirmerVider(false);
    setOccupe(true);
    generation.current += 1;
    voulu.current.clear();
    setItems([]);
    setUnread(0);
    ecrituresEnCours.current += 1;
    try {
      const r = await deleteAllNotifications();
      if (!r.ok) {
        alert(`Suppression impossible : ${r.error ?? "erreur inconnue"}`);
        await chargerListe(false);
      }
    } finally {
      ecrituresEnCours.current -= 1;
    }
    if (stable()) await recompter();
    setOccupe(false);
  }

  const nb = items?.length ?? 0;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={handleOpen}
        className="relative w-9 h-9 flex items-center justify-center rounded-xl border border-border-default bg-surface hover:bg-surface-secondary transition-colors text-text-secondary bell-swing"
        title="Notifications"
        aria-label="Notifications"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-negative text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <style>{CSS}</style>
          {/* LOT 208 fix — Backdrop pour click-outside + visibilité */}
          <div
            className="fixed inset-0 z-[55] sm:hidden"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          {/* LOT 208 fix — Position fixed sur mobile (full-width margin)
              + absolute droite sur desktop. Évite le hors-écran. */}
          <div
            className="
              fixed bottom-[calc(4.75rem+env(safe-area-inset-bottom,0px))] left-2 right-2 mx-auto max-w-[420px]
              sm:absolute sm:bottom-auto sm:top-full sm:left-0 sm:right-auto sm:mt-2 sm:max-w-[380px] sm:w-[380px]
              bg-surface border border-border-default rounded-2xl shadow-xl z-[60] overflow-hidden
            "
          >
          {/* Header — la suppression de masse passe par une confirmation */}
          <div className="px-4 py-3 border-b border-border-default/50">
            {confirmerVider ? (
              <div className="flex items-center justify-between gap-2">
                <p className="text-[12px] font-semibold text-negative">Supprimer toutes les notifications ?</p>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => setConfirmerVider(false)}
                    className="text-[11px] px-2 py-1 rounded-md text-text-muted hover:text-foreground transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={toutSupprimer}
                    className="text-[11px] px-2.5 py-1 rounded-md bg-negative text-white font-semibold hover:opacity-90 transition-opacity"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-bold">Notifications</h3>
                <div className="flex items-center gap-3">
                  {unread > 0 && (
                    <button
                      type="button"
                      onClick={toutMarquerLu}
                      disabled={occupe}
                      className="text-[11px] text-forest font-semibold hover:underline disabled:opacity-50"
                    >
                      Tout marquer lu
                    </button>
                  )}
                  {nb > 0 && (
                    <button
                      type="button"
                      onClick={() => setConfirmerVider(true)}
                      disabled={occupe}
                      className="text-[11px] text-text-muted font-semibold hover:text-negative hover:underline disabled:opacity-50"
                    >
                      Tout supprimer
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Body */}
          <div className="max-h-[60vh] overflow-y-auto overflow-x-hidden">
            {loading ? (
              <p className="px-4 py-8 text-center text-text-muted text-sm">Chargement…</p>
            ) : items === null || items.length === 0 ? (
              <div className="px-4 py-10 text-center space-y-2">
                <p className="text-3xl">🔔</p>
                <p className="text-sm text-text-muted">Aucune notification.</p>
                <p className="text-[11px] text-text-muted">
                  Configure tes alertes dans Réglages → Notifications.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-border-default/50">
                {items.map((n) => (
                  <LigneNotification
                    key={n.id}
                    n={n}
                    meta={TYPE_LABELS[n.type] ?? META_DEFAUT}
                    onBasculer={() => void basculerLue(n)}
                    onOuvrirLien={() => ouvrirLien(n)}
                    onOuvrirExterne={(url) => void ouvrirLienExterne(n, url)}
                    onLire={() => void marquerLue(n, false)}
                    onSupprimer={() => void supprimer(n.id)}
                  />
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-border-default/50 bg-surface-elevated/30 flex items-center justify-between gap-3">
            <Link
              href="/budget/reglages"
              prefetch={false}
              className="text-[11px] text-text-muted hover:text-foreground transition-colors"
              onClick={() => setOpen(false)}
            >
              ⚙ Préférences notifications
            </Link>
            {nb > 0 && <span className="text-[10px] text-text-muted">← glisser pour supprimer</span>}
          </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ───────────────────────── Une notification ───────────────────────── */

function IconeNotification({ meta, nonLue, onBasculer }: { meta: Meta; nonLue: boolean; onBasculer: () => void }) {
  // Compteur de gestes : sert de clé pour rejouer la gerbe (allumage) ou
  // l'anneau qui s'estompe (extinction) à chaque bascule.
  const [gestes, setGestes] = useState(0);
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setGestes((g) => g + 1); onBasculer(); }}
      className="notif-icone relative shrink-0 grid place-items-center w-9 h-9 rounded-full"
      data-allumee={nonLue ? "oui" : "non"}
      style={{ "--teinte": meta.teinte } as CSSProperties}
      title={nonLue ? "Marquer comme lue" : "Marquer comme non lue"}
      aria-label={nonLue ? "Non lue — marquer comme lue" : "Lue — marquer comme non lue"}
      aria-pressed={nonLue}
    >
      <span className="notif-emoji text-[15px] leading-none" aria-hidden>{meta.emoji}</span>
      {nonLue && (
        <span className="notif-bulles" aria-hidden><i /><i /><i /><i /></span>
      )}
      {gestes > 0 && nonLue && (
        <span key={`salve-${gestes}`} className="notif-salve" aria-hidden><i /><i /><i /><i /><i /><i /></span>
      )}
      {gestes > 0 && !nonLue && <span key={`extinction-${gestes}`} className="notif-extinction" aria-hidden />}
    </button>
  );
}

type Phase = "repos" | "glisse" | "retour" | "sortie" | "effondre";

function LigneNotification({ n, meta, onBasculer, onOuvrirLien, onOuvrirExterne, onLire, onSupprimer }: {
  n: NotificationItem;
  meta: Meta;
  onBasculer: () => void;
  onOuvrirLien: () => void;
  onOuvrirExterne: (url: string) => void;
  onLire: () => void;
  onSupprimer: () => void;
}) {
  const ligneRef = useRef<HTMLLIElement>(null);
  const geste = useRef<{ id: number; x0: number; y0: number; t0: number; largeur: number; actif: boolean } | null>(null);
  const decalageRef = useRef(0);
  // Un glissement se termine par un « click » : il ne doit pas ouvrir le lien.
  const vientDeGlisser = useRef(false);
  const [decalage, setDecalage] = useState(0);
  const [largeur, setLargeur] = useState(360);
  const [phase, setPhase] = useState<Phase>("repos");

  const nonLue = !n.read_at;
  const url = n.target_url;
  const seuilPx = largeur * SEUIL_SUPPRESSION;
  const progression = Math.min(1, -decalage / seuilPx);
  const auDela = -decalage >= seuilPx;

  function poser(v: number) {
    decalageRef.current = v;
    setDecalage(v);
  }

  function supprimerAnime() {
    if (phase === "sortie" || phase === "effondre") return;
    const w = ligneRef.current?.offsetWidth ?? largeur;
    setLargeur(w);
    setPhase("sortie");
    poser(-w);
    // La ligne file à gauche, puis se referme : les suivantes remontent.
    window.setTimeout(() => setPhase("effondre"), 230);
    window.setTimeout(onSupprimer, 470);
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    vientDeGlisser.current = false;
    if (phase === "sortie" || phase === "effondre") return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    geste.current = { id: e.pointerId, x0: e.clientX, y0: e.clientY, t0: performance.now(), largeur: e.currentTarget.offsetWidth, actif: false };
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const g = geste.current;
    if (!g || e.pointerId !== g.id) return;
    const dx = e.clientX - g.x0;
    const dy = e.clientY - g.y0;
    if (!g.actif) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      // Geste vertical (défilement de la liste) ou vers la droite : pas pour nous.
      if (Math.abs(dy) >= Math.abs(dx) || dx > 0) { geste.current = null; return; }
      g.actif = true;
      vientDeGlisser.current = true;
      setLargeur(g.largeur);
      setPhase("glisse");
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* pointeur déjà relâché */ }
    }
    // Au-delà du bord droit : résistance élastique, comme sur iOS.
    poser(dx > 0 ? dx * 0.2 : dx);
  }

  function finir(annule: boolean) {
    const g = geste.current;
    geste.current = null;
    if (!g || !g.actif) return;
    const d = decalageRef.current;
    const vitesse = d / Math.max(1, performance.now() - g.t0); // px/ms, négatif vers la gauche
    if (!annule && (-d >= g.largeur * SEUIL_SUPPRESSION || (vitesse < -0.9 && -d > 48))) {
      supprimerAnime();
    } else {
      setPhase("retour");
      poser(0);
    }
  }

  const transition =
    phase === "glisse" ? "none"
    : phase === "sortie" ? "transform 220ms cubic-bezier(.4,0,1,1)"
    : "transform 420ms cubic-bezier(.18,1.25,.4,1)";

  const contenu = (
    <>
      <span className={`block text-[13px] ${nonLue ? "font-semibold text-foreground" : "text-text-secondary"}`}>
        {n.title}
      </span>
      {n.body && <span className="block text-[11px] text-text-muted">{n.body}</span>}
      <span className="block text-[10px] text-text-muted">{formatRelative(n.created_at)}</span>
    </>
  );
  const classeContenu = "flex-1 min-w-0 space-y-0.5 text-left";

  return (
    <li
      ref={ligneRef}
      className="grid transition-[grid-template-rows,opacity] duration-200 ease-out"
      style={{ gridTemplateRows: phase === "effondre" ? "0fr" : "1fr", opacity: phase === "effondre" ? 0 : 1 }}
      onKeyDown={(e) => {
        // Au clavier, pas de glissement possible : Suppr fait le même travail.
        if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); supprimerAnime(); }
      }}
    >
      <div className="overflow-hidden">
        <div className="relative">
          {decalage < 0 && (
            <div className="absolute inset-0 bg-negative flex items-center justify-end pr-6" aria-hidden>
              <span
                className="text-white transition-transform duration-150 ease-out"
                style={{
                  // Passé le seuil, la corbeille grossit et suit le bord de la ligne.
                  transform: `translateX(${auDela ? Math.min(0, decalage + seuilPx) : 0}px) scale(${auDela ? 1.15 : 0.6 + 0.4 * progression})`,
                  opacity: Math.max(0.3, progression),
                }}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                </svg>
              </span>
            </div>
          )}
          <div
            className={`relative flex items-start gap-3 px-4 py-3 touch-pan-y ${phase === "glisse" ? "select-none cursor-grabbing" : ""}`}
            style={{
              transform: `translateX(${decalage}px)`,
              transition: `${transition}, background-color .4s ease`,
              backgroundColor: nonLue ? `color-mix(in srgb, ${meta.teinte} 7%, var(--g-surface))` : "var(--g-surface)",
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={() => finir(false)}
            onPointerCancel={() => finir(true)}
            onTransitionEnd={(e) => { if (e.propertyName === "transform" && phase === "retour") setPhase("repos"); }}
            onClickCapture={(e) => {
              if (!vientDeGlisser.current) return;
              e.preventDefault();
              e.stopPropagation();
              vientDeGlisser.current = false;
            }}
          >
            <IconeNotification meta={meta} nonLue={nonLue} onBasculer={onBasculer} />
            {url && estInterne(url) ? (
              <Link href={url} prefetch={false} draggable={false} onClick={onOuvrirLien} className={classeContenu}>
                {contenu}
              </Link>
            ) : url ? (
              <a
                href={url}
                draggable={false}
                onClick={(e) => { e.preventDefault(); onOuvrirExterne(url); }}
                className={classeContenu}
              >
                {contenu}
              </a>
            ) : (
              // Sans lien, ouvrir = lire : un clic sur le texte la marque lue.
              <button type="button" onClick={onLire} className={`${classeContenu} ${nonLue ? "cursor-pointer" : "cursor-default"}`}>
                {contenu}
              </button>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}
